// State and availability of the main menu drawer, shared by the header (hamburger) and the drawer.
//
// The event menu: the event sections (World Congress events only) and the user's own event pages.
// Any logged-in user gets the menu; the event sections only appear inside a World Congress event, and
// not while the email still needs verifying, since every destination in them sits behind that.
// The account (settings, theme, sign out) is the separate avatar menu, see accountOpen.
export const useMainMenu = () => {
  const open = useState<boolean>('wad4-main-menu-open', () => false);
  // The account menu (avatar, top right) is a second drawer, from the right. Its open state lives here so
  // the header button and the drawer share it, like the main menu's.
  const accountOpen = useState<boolean>('wad4-account-menu-open', () => false);
  const route = useRoute();
  const {userLoggedIn, requireEmailVerification} = useWadAuth();
  const eventsStore = useEventsStore();
  const {eventData} = storeToRefs(eventsStore);

  const eventId = computed(() => eventData.value?.id ?? null);
  const routeName = computed(() => <string>route.name);

  const isGlobalPage = computed(() =>
    !routeName.value || ['index', 'user-login', 'user-signup', 'user-link-ticket', 'user-verification'].includes(routeName.value)
  );

  // Where the logo in the menu leads: the event's home page, or the event list outside an event.
  const homeRoute = computed(() =>
    eventId.value && routeName.value !== 'index'
      ? {name: 'events-eventId', params: {eventId: eventId.value}}
      : {name: 'index'}
  );

  const available = computed(() => userLoggedIn.value);

  // Inside an event: the user's own event pages (profile, contacts, leads) make sense. On the global
  // pages (event picker, login) the last event is only a leftover in the store, and offering "My
  // Profile" for it, without saying which event, is confusing.
  const hasEventContext = computed(() =>
    !!eventId.value && !isGlobalPage.value && !requireEmailVerification.value
  );

  // The event guide sections themselves belong to World Congress events.
  const hasEventSections = computed(() =>
    hasEventContext.value && eventData.value?.event_type === wadConstants.EVENT_TYPE.WORLD_CONGRESS
  );

  return {open, accountOpen, available, hasEventContext, hasEventSections, homeRoute, eventId, routeName};
};
